import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";
import { fmtDateTime } from "../utils/time";

const B    = "#2C1A0E";
const ROSE = "#C9847A";

const CATEGORIES = ["revenue","cogs","opex","marketing","admin"];
const CAT_LABELS = {
  revenue:"💚 Revenue", cogs:"🟠 Cost of Goods",
  opex:"🔵 Salaries & Ops", marketing:"🟣 Marketing", admin:"⬜ Admin & Tools"
};

// Known panel line items per category
const LINE_ITEMS = {
  revenue: ["B2C – Women","B2C – Men","B2C – Kids","B2B – Corporate",
            "Workshop – Self-hosted","B2C – Other"],
  cogs:    ["Raw Materials","Packaging & Finishing","Shipping & Courier","Workshop Supplies"],
  opex:    ["Artist 1 Salary","Artist 2 Salary","Founder Draw"],
  marketing:["Digital Ads","Branding – Gifts/Charms","Flea / Pop-up Stall","Photography & Content"],
  admin:   ["Website & Domain","Zoho Books","Miscellaneous"] };

const SKUS = [
  null,"W01","W02","W03","W04","M01","M02","M03","M04","K01","K02"
];

export default function Zoho() {
  const [status,   setStatus]   = useState(null);
  const [remote,   setRemote]   = useState(null);
  const [mappings, setMappings] = useState([]);
  const [syncing,  setSyncing]  = useState(false);
  const [syncLog,  setSyncLog]  = useState([]);
  const [tab,      setTab]      = useState("overview"); // overview | mappings
  const [saving,   setSaving]   = useState(false);
  const [filter,   setFilter]   = useState("all"); // all | unconfirmed

  const loadStatus = useCallback(async () => {
    const r = await apiFetch("/api/zoho/status");
    if (r.ok) setStatus(await r.json());
    const rr = await apiFetch("/api/zoho/remote-status");
    if (rr.ok) setRemote(await rr.json());
  }, []);

  const loadMappings = useCallback(async () => {
    const r = await apiFetch("/api/zoho/mappings");
    if (r.ok) setMappings(await r.json());
  }, []);

  useEffect(() => {
    loadStatus();
    loadMappings();
  }, []);

  // Listen for OAuth popup completing
  useEffect(() => {
    const handler = (e) => {
      if (e.data === "zoho_connected") {
        loadStatus();
        alert("✅ Zoho Books connected!");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const connectZoho = async () => {
    const r = await apiFetch("/api/zoho/auth-url");
    const { url } = await r.json();
    window.open(url, "zoho_auth", "width=600,height=700,left=200,top=100");
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Books? Synced data stays in the panel.")) return;
    await apiFetch("/api/zoho/disconnect", { method: "POST" });
    loadStatus();
  };

  const runSync = async (endpoint = "/api/zoho/sync") => {
    setSyncing(true); setSyncLog(["⏳ Sync started, running in background…"]);
    try {
      const r = await apiFetch(endpoint, { method: "POST" });
      const d = await r.json();
      if (!d.ok && d.error) { setSyncLog([`❌ ${d.error}`]); setSyncing(false); return; }
      const poll = async () => {
        const pr = await apiFetch("/api/zoho/sync/poll");
        const pd = await pr.json();
        if (pd.log?.length) setSyncLog(pd.log);
        if (pd.status === "running") {
          setTimeout(poll, 3000);
        } else {
          setSyncing(false);
          loadStatus();
        }
      };
      setTimeout(poll, 3000);
    } catch(e) {
      setSyncLog([`❌ ${e.message}`]); setSyncing(false);
    }
  };

  const updateMapping = (idx, field, val) => {
    setMappings(m => m.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  };

  const saveAllMappings = async () => {
    setSaving(true);
    await apiFetch("/api/zoho/mappings/bulk", {
      method: "POST",
      body: JSON.stringify(mappings.map(m => ({ ...m, confirmed: true }))) });
    setSaving(false);
    loadMappings();
  };

  const confirmSingle = async (m) => {
    await apiFetch("/api/zoho/mappings", {
      method: "POST",
      body: JSON.stringify({ ...m, confirmed: true }) });
    loadMappings();
  };

  const visible = filter === "unconfirmed"
    ? mappings.filter(m => !m.confirmed)
    : mappings;

  return (
    <div style={{ padding: "28px 28px 60px", fontFamily: "Inter, sans-serif" }}>
      <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                   color: B, fontSize: 26, fontWeight: 700, marginBottom: 4 }}>
        🔗 Zoho Books Integration
      </h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 24 }}>
        Sync invoices, expenses and contacts from Zoho Books into the panel automatically.
      </p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24,
                    borderBottom: "2px solid #EDE0D4" }}>
        {[["overview","📊 Overview"],["mappings","🗂️ SKU Reference"]].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: "8px 18px", border: "none", cursor: "pointer",
            background: tab === key ? "#fff" : "transparent",
            color: tab === key ? B : "#888",
            fontWeight: tab === key ? 700 : 400,
            borderBottom: tab === key ? `2px solid ${B}` : "2px solid transparent",
            marginBottom: -2, fontSize: 13 }}>{label}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === "overview" && (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

          {/* Connection card */}
          <div style={card}>
            {remote?.ok && (
            <div style={{ display:"flex", gap:10, marginBottom:18 }}>
              {[["📄","Invoices",remote.invoices],["👥","Contacts",remote.contacts],["🧾","Expenses",remote.expenses]].map(([icon,label,count]) => (
                <div key={label} style={{ flex:1, textAlign:"center", background:"#F7F5F2",
                                          borderRadius:8, padding:"10px 6px" }}>
                  <div style={{ fontSize:18 }}>{icon}</div>
                  <div style={{ fontSize:20, fontWeight:700, color:B }}>{count ?? "—"}</div>
                  <div style={{ fontSize:11, color:"#888" }}>{label}</div>
                  <div style={{ fontSize:9, color:"#bbb", marginTop:2 }}>live from Zoho</div>
                </div>
              ))}
            </div>
          )}
          <div style={cardTitle}>Connection</div>
            {status ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: 28 }}>{status.connected ? "✅" : "🔴"}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: B }}>
                      {status.connected ? "Connected" : "Not Connected"}
                    </div>
                    {status.connected_at && (
                      <div style={{ fontSize: 12, color: "#888" }}>
                        Since {fmtDateTime(status.connected_at)}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 16,
                              background: "#F7F5F2", padding: "8px 12px", borderRadius: 6 }}>
                  <div>Org ID: <strong>60060673992</strong></div>
                  <div>Region: <strong>zoho.in</strong></div>
                  <div>Mappings: <strong>{status.total_mappings}</strong>
                    {status.unconfirmed > 0 && (
                      <span style={{ color: "#E67E22", marginLeft: 6 }}>
                        ({status.unconfirmed} need review)
                      </span>
                    )}
                  </div>
                </div>
                {status.connected ? (
                  <button onClick={disconnect} style={btnDanger}>Disconnect</button>
                ) : (
                  <button onClick={connectZoho} style={btnPrimary}>
                    Connect Zoho Books →
                  </button>
                )}
              </>
            ) : (
              <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>
            )}
          </div>

          {/* Setup instructions */}
          {!status?.connected && (
            <div style={{ ...card, flex: 2, minWidth: 320 }}>
              <div style={cardTitle}>Setup Instructions</div>
              <ol style={{ paddingLeft: 18, fontSize: 13, lineHeight: 2, color: "#444" }}>
                <li>Go to <a href="https://api-console.zoho.in" target="_blank" rel="noreferrer"
                    style={{ color: "#2F5496" }}>api-console.zoho.in</a></li>
                <li>Create a <strong>Server-based Application</strong></li>
                <li>Set redirect URI to:<br/>
                  <code style={codeStyle}>
                    https://embroideary-app-production.up.railway.app/api/zoho/callback
                  </code>
                </li>
                <li>Copy Client ID and Client Secret</li>
                <li>Add to Railway env vars:<br/>
                  <code style={codeStyle}>ZOHO_CLIENT_ID=...</code><br/>
                  <code style={codeStyle}>ZOHO_CLIENT_SECRET=...</code>
                </li>
                <li>Click <strong>Connect Zoho Books</strong> above</li>
              </ol>
            </div>
          )}

          {/* Sync card */}
          {status?.connected && (
            <div style={card}>
              <div style={cardTitle}>Sync Data</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                Last sync: <strong>{status.last_sync_at
                  ? fmtDateTime(status.last_sync_at)
                  : "Never"}</strong>
                <br/>Auto-sync runs every <strong>6 hours</strong>.
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={() => runSync("/api/zoho/sync")}
                  disabled={syncing} style={{ ...btnPrimary, width: "100%" }}>
                  {syncing ? "⏳ Syncing…" : "🔄 Full Sync (All Data)"}
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => runSync("/api/zoho/sync/invoices")}
                    disabled={syncing} style={{ ...btnSecondary, flex: 1 }}>
                    📄 Invoices
                  </button>
                  <button onClick={() => runSync("/api/zoho/sync/expenses")}
                    disabled={syncing} style={{ ...btnSecondary, flex: 1 }}>
                    💸 Expenses
                  </button>
                  <button onClick={() => runSync("/api/zoho/sync/contacts")}
                    disabled={syncing} style={{ ...btnSecondary, flex: 1 }}>
                    👥 Contacts
                  </button>
                </div>
              </div>

              {/* Sync log */}
              {syncLog.length > 0 && (
                <div style={{ marginTop: 14, background: "#1A2E1A", borderRadius: 8,
                              padding: "10px 14px", maxHeight: 180, overflowY: "auto" }}>
                  {syncLog.map((line, i) => (
                    <div key={i} style={{
                      fontSize: 12, fontFamily: "monospace", marginBottom: 3,
                      color: line.startsWith("✅") ? "#7EC8A0"
                           : line.startsWith("❌") ? "#E88"
                           : line.startsWith("⚠️") ? "#F4C842" : "#ccc"
                    }}>{line}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Last sync log from DB */}
          {status?.connected && status?.last_sync_log?.length > 0 && syncLog.length === 0 && (
            <div style={{ ...card, width: "100%" }}>
              <div style={cardTitle}>Last Sync Result</div>
              <div style={{ background: "#1A2E1A", borderRadius: 8,
                            padding: "10px 14px" }}>
                {status.last_sync_log.map((line, i) => (
                  <div key={i} style={{
                    fontSize: 12, fontFamily: "monospace", marginBottom: 3,
                    color: line.startsWith("✅") ? "#7EC8A0"
                         : line.startsWith("❌") ? "#E88"
                         : line.startsWith("⚠️") ? "#F4C842" : "#ccc"
                  }}>{line}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MAPPINGS TAB ── */}
      {tab === "mappings" && (
        <div>
          <p style={{ fontSize:13, color:"#666", marginBottom:20, lineHeight:1.7 }}>
            Revenue is mapped via the <strong>SKU field</strong> on invoice line items.
            Expenses are mapped via the <strong>Expense Account</strong> field.
            Both are automatic — no manual review needed.
          </p>

          <div style={{ marginBottom:28 }}>
            <div style={{ fontWeight:700, color:B, marginBottom:10, fontSize:14 }}>Revenue — SKU → Line Item</div>
            <div style={{ overflowX:"auto", border:"1px solid #EDE0D4", borderRadius:10 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, background:"#fff" }}>
                <thead><tr style={{ background:"#F7F5F2" }}>
                  <th style={th}>Zoho SKU Value</th>
                  <th style={th}>AOP Revenue Line</th>
                </tr></thead>
                <tbody>
                  {(mappings.sku_mappings || []).map(m => (
                    <tr key={m.sku} style={{ borderBottom:"1px solid #F0E8E0" }}>
                      <td style={{ ...td, fontFamily:"monospace", color:"#555" }}>{m.sku}</td>
                      <td style={{ ...td, fontWeight:600, color:B }}>{m.line_item}</td>
                    </tr>
                  ))}
                  <tr style={{ background:"#FEF9C3" }}>
                    <td style={{ ...td, fontStyle:"italic", color:"#888" }}>(empty / unknown)</td>
                    <td style={{ ...td, fontWeight:600, color:B }}>B2C – Direct</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div style={{ fontWeight:700, color:B, marginBottom:10, fontSize:14 }}>Expenses — Account → Line Item</div>
            <div style={{ overflowX:"auto", border:"1px solid #EDE0D4", borderRadius:10 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, background:"#fff" }}>
                <thead><tr style={{ background:"#F7F5F2" }}>
                  <th style={th}>Zoho Account Name</th>
                  <th style={th}>AOP Line Item</th>
                  <th style={th}>Category</th>
                </tr></thead>
                <tbody>
                  {(mappings.expense_mappings || []).map(m => (
                    <tr key={m.account} style={{ borderBottom:"1px solid #F0E8E0" }}>
                      <td style={{ ...td, color:"#555" }}>{m.account}</td>
                      <td style={{ ...td, fontWeight:600, color:B }}>{m.line_item}</td>
                      <td style={td}>
                        <span style={{
                          background: m.category==="cogs" ? "#DCFCE7" : m.category==="opex" ? "#DBEAFE"
                                    : m.category==="marketing" ? "#EDE9FE" : "#FEF9C3",
                          color: m.category==="cogs" ? "#15803D" : m.category==="opex" ? "#1D4ED8"
                               : m.category==="marketing" ? "#6D28D9" : "#854D0E",
                          borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600,
                        }}>{m.category}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const card      = { background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12,
                    padding: "20px 22px", minWidth: 260, flex: 1 };
const cardTitle = { fontFamily: "'Cormorant Garamond',Georgia,serif",
                    fontSize: 16, fontWeight: 700, color: "#2C1A0E", marginBottom: 14 };
const codeStyle = { display: "inline-block", background: "#F0EBE3", padding: "2px 7px",
                    borderRadius: 4, fontSize: 11, fontFamily: "monospace",
                    color: "#5C3317", margin: "2px 0" };
const btnPrimary  = { background: "#2C1A0E", color: "#FAF7F2", border: "none",
                      borderRadius: 7, padding: "9px 18px", fontSize: 13,
                      fontWeight: 600, cursor: "pointer" };
const btnSecondary= { background: "#F2EDE8", color: "#2C1A0E", border: "1px solid #DDD4C8",
                      borderRadius: 7, padding: "7px 10px", fontSize: 12, cursor: "pointer" };
const btnDanger   = { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5",
                      borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer" };
const sel         = { padding: "5px 8px", border: "1px solid #DDD4C8", borderRadius: 6,
                      fontSize: 12, background: "#fff", flex: 1, minWidth: 0 };

const th = { padding:"8px 14px", textAlign:"left", fontWeight:600, color:"#666",
             fontSize:11, textTransform:"uppercase", letterSpacing:0.5 };
const td = { padding:"10px 14px", verticalAlign:"middle" };
